import type { FlowAccessStatus } from '@/services/admin.service';

/**
 * The app-entry gate: what AppLayout should render for a given auth/access
 * state. Extracted as a pure function so the decision is unit-testable without
 * mounting React (the test environment is node, not jsdom).
 *
 *   loading          → the auth providers are still initializing
 *   redirect-login   → not authenticated with Supabase OR Microsoft
 *   awaiting-session → authenticated with Microsoft, but the Supabase session
 *                      (minted asynchronously by the token-exchange bridge) does
 *                      not exist yet — hold, do NOT run the access check
 *   verifying        → the access-check RPC is in flight (or retrying)
 *   unverifiable     → a real Supabase session whose token still lacks an email
 *                      claim (rare, persistent) — recoverable via re-auth
 *   denied           → the token's email is genuinely not on the allowlist
 *   granted          → render the app
 */
export type AccessGate =
  | 'loading'
  | 'redirect-login'
  | 'awaiting-session'
  | 'verifying'
  | 'unverifiable'
  | 'denied'
  | 'granted';

export interface AccessGateInput {
  /** Supabase auth still initializing (authStore.loading). */
  authLoading: boolean;
  /** MSAL interaction in progress (useMicrosoftAuth.isLoading). */
  msLoading: boolean;
  /** A Supabase session exists — i.e. requests carry the user's JWT email. */
  hasSupabaseSession: boolean;
  /** The user is authenticated with Microsoft (Entra) via MSAL. */
  isMsAuthenticated: boolean;
  /** The access-check query is actively fetching. */
  isCheckingAccess: boolean;
  /** The access-check query errored (all retries exhausted). */
  isAccessError: boolean;
  /** The resolved access-check result, if any. */
  accessStatus: FlowAccessStatus | undefined;
}

/**
 * Decide the entry gate.
 *
 * The key rule — and the fix for the transient "Couldn't verify your access"
 * screen — is that the access check is only meaningful once a Supabase session
 * exists. `check_flow_access()` reads `auth.jwt() ->> 'email'`; a
 * Microsoft-SSO user has that email only after the token-exchange bridge mints
 * a Supabase session. Running the RPC before then sends it on the anon key,
 * where the JWT has no email, yielding a spurious 'unverifiable'. So while a
 * Microsoft user is waiting for their Supabase session we report
 * 'awaiting-session' (a recoverable spinner) instead of ever surfacing that
 * false negative, and the app is never rendered without an explicit grant.
 */
export function resolveAccessGate(s: AccessGateInput): AccessGate {
  if (s.authLoading || s.msLoading) return 'loading';

  if (!s.hasSupabaseSession && !s.isMsAuthenticated) return 'redirect-login';

  // Microsoft-authenticated but no Supabase session yet: the bridge is in
  // flight. Never run the access check on the anon key — wait for the session.
  if (!s.hasSupabaseSession) return 'awaiting-session';

  // Session exists: the access-check RPC will carry the JWT email.
  if (s.isCheckingAccess || s.isAccessError) return 'verifying';

  if (s.accessStatus === 'denied') return 'denied';
  if (s.accessStatus === 'allowed') return 'granted';
  if (s.accessStatus === 'unverifiable') return 'unverifiable';

  // No definitive result yet (e.g. the query is between enabled and its first
  // fetch). Never render the app without an explicit grant — hold on a spinner.
  return 'verifying';
}
